// Correct a committed vendor quote.
//
// WHY THIS EXISTS. 98% of production vendor quotes were written by the AI
// parser, and until now a committed quote had no update route of any kind — a
// mistyped price or a missing lead time was permanent. The full field set has
// always existed in parse-vendor-quote-modal.tsx, but only before commit.
//
// WHAT IT DELIBERATELY DOES NOT DO. It does not touch provenance (`source`,
// `parserConfidence`, `parserModel`, the raw parsed blob) or the triage review
// status. A human fixing a parsed quote must not be able to relabel it as
// hand-entered or rewrite what the parser actually saw — that record is the
// evidence behind the confidence thresholds elsewhere in the system.
//
// THE IMPACT PANEL IS THE POINT. Customer quotes and vendor POs freeze their
// pricing and their rendered HTML when they are issued. That is correct: a
// customer's quote must not change under them after the fact. It is also a
// trap, because fixing a price here leaves a SENT PO quoting the old one with
// nothing on screen to say so. So the documents already built from this quote
// are listed before the save and again after it, split into the ones that can
// be regenerated and the ones that are now permanently out of step.

import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, AlertTriangle, FileText, Save } from 'lucide-react';
import {
  api,
  type VendorQuote,
  type VendorQuoteImpactResponse,
  type PatchVendorQuoteResponse,
  type DownstreamDoc,
  type ConditionCode,
  type CertType,
  type Currency,
  type Incoterms2020,
} from '@/lib/api';

// Same literal lists parse-vendor-quote-modal.tsx uses, so the correction form
// offers exactly the values the commit form does.
const CONDITIONS: ConditionCode[] = ['NE', 'NS', 'FN', 'OH', 'SV', 'AR', 'RP'];
const CERTS: CertType[] = ['faa_8130_3', 'easa_form_1', 'dual_release', 'coc', 'none'];
const INCOTERMS: Incoterms2020[] =
  ['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP', 'FAS', 'FOB', 'CFR', 'CIF'];
const CURRENCIES: Currency[] = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'AED', 'SGD'];

interface Props {
  quote: VendorQuote;
  onClose: () => void;
}

/** Only the fields the PATCH route accepts, in the order a broker reads them. */
interface Draft {
  vendorNameRaw: string;
  vendorContactName: string;
  vendorContactEmail: string;
  unitPrice: string;
  currency: string;
  qtyOffered: string;
  conditionOffered: string;
  certOffered: string;
  leadTimeDays: string;
  incoterms: string;
  fobLocation: string;
  eccn: string;
  htsCode: string;
  countryOfOrigin: string;
  hazmatClass: string;
  notes: string;
}

const seed = (q: VendorQuote): Draft => ({
  vendorNameRaw: q.vendorNameRaw ?? '',
  vendorContactName: q.vendorContactName ?? '',
  vendorContactEmail: q.vendorContactEmail ?? '',
  unitPrice: (q.unitPriceCents / 100).toFixed(2),
  currency: q.currency ?? 'USD',
  qtyOffered: String(q.qtyOffered ?? 1),
  conditionOffered: q.conditionOffered ?? '',
  certOffered: q.certOffered ?? '',
  leadTimeDays: q.leadTimeDays == null ? '' : String(q.leadTimeDays),
  incoterms: q.incoterms ?? '',
  fobLocation: q.fobLocation ?? '',
  eccn: q.eccn ?? '',
  htsCode: q.htsCode ?? '',
  countryOfOrigin: q.countryOfOrigin ?? '',
  hazmatClass: q.hazmatClass ?? '',
  notes: q.notes ?? '',
});

function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-foreground-muted mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-foreground-muted mt-0.5">{hint}</span>}
    </label>
  );
}

const inputCls =
  'w-full rounded border border-border bg-surface px-2 py-1 text-xs text-foreground ' +
  'focus:outline-none focus:ring-1 focus:ring-primary';

function DocList({ docs }: { docs: DownstreamDoc[] }) {
  const frozen = docs.filter((d) => !d.refreshable);
  const refreshable = docs.filter((d) => d.refreshable);
  return (
    <div className="space-y-1">
      {frozen.length > 0 && (
        <div className="rounded bg-warning-muted p-2">
          <div className="flex items-center gap-1.5 text-warning font-medium">
            <AlertTriangle className="w-3.5 h-3.5" />
            {frozen.length} issued document{frozen.length === 1 ? '' : 's'} will NOT change
          </div>
          <ul className="mt-1 space-y-0.5 text-foreground-muted">
            {frozen.map((d) => (
              <li key={d.publicId} className="font-mono text-[11px]">
                {d.ref} · {d.kind === 'vendor_po' ? 'vendor PO' : 'customer quote'} · {d.status}
              </li>
            ))}
          </ul>
          <div className="text-[10px] text-foreground-muted mt-1">
            These were sent with the old values and stay exactly as issued. If the difference
            matters, reissue rather than editing history.
          </div>
        </div>
      )}
      {refreshable.length > 0 && (
        <div className="rounded bg-surface-muted p-2">
          <div className="flex items-center gap-1.5 text-foreground font-medium">
            <FileText className="w-3.5 h-3.5" />
            {refreshable.length} draft{refreshable.length === 1 ? '' : 's'} can pick this up
          </div>
          <ul className="mt-1 space-y-0.5 text-foreground-muted">
            {refreshable.map((d) => (
              <li key={d.publicId} className="font-mono text-[11px]">
                {d.ref} · {d.kind === 'vendor_po' ? 'vendor PO' : 'customer quote'} · draft
              </li>
            ))}
          </ul>
          <div className="text-[10px] text-foreground-muted mt-1">
            Open each draft and regenerate it to apply the correction.
          </div>
        </div>
      )}
    </div>
  );
}

export function EditVendorQuoteModal({ quote, onClose }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => seed(quote));
  const [saved, setSaved] = useState<PatchVendorQuoteResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-seed if the row changes underneath (a refetch, a different quote).
  useEffect(() => setDraft(seed(quote)), [quote]);

  const impact = useQuery({
    queryKey: ['vendor-quote-impact', quote.id],
    queryFn: () =>
      api<VendorQuoteImpactResponse>(`/api/vendor-quotes/${quote.id}/impact`, {
        tokenGetter: () => getToken(),
      }),
  });

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<PatchVendorQuoteResponse>(`/api/vendor-quotes/${quote.id}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify(body),
      }),
    onSuccess: (r) => {
      setSaved(r);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['vendor-quotes'] });
      void qc.invalidateQueries({ queryKey: ['rfq'] });
    },
    onError: (e: unknown) =>
      setError((e as { message?: string })?.message ?? 'The correction could not be saved.'),
  });

  /** Diff-only body, matching the pattern in customer-detail.tsx: send what
   *  changed and nothing else, so a no-op save writes no audit row. */
  function build(): Record<string, unknown> {
    const b: Record<string, unknown> = {};
    const text = (k: keyof Draft, cur: string | null | undefined, field = k) => {
      const v = draft[k].trim();
      const now = v === '' ? null : v;
      if (now !== (cur ?? null)) b[field] = now;
    };

    if (draft.vendorNameRaw.trim() && draft.vendorNameRaw.trim() !== quote.vendorNameRaw) {
      b.vendorNameRaw = draft.vendorNameRaw.trim();
    }
    text('vendorContactName', quote.vendorContactName);
    text('vendorContactEmail', quote.vendorContactEmail);

    const cents = Math.round(Number(draft.unitPrice) * 100);
    if (Number.isFinite(cents) && cents >= 0 && cents !== quote.unitPriceCents) {
      b.unitPriceCents = cents;
    }
    if (draft.currency && draft.currency !== quote.currency) b.currency = draft.currency;

    const qty = Number(draft.qtyOffered);
    if (Number.isInteger(qty) && qty >= 1 && qty !== quote.qtyOffered) b.qtyOffered = qty;

    const enumField = (k: keyof Draft, cur: string | null) => {
      const v = draft[k] || null;
      if (v !== (cur ?? null)) b[k] = v;
    };
    enumField('conditionOffered', quote.conditionOffered);
    enumField('certOffered', quote.certOffered);
    enumField('incoterms', quote.incoterms);

    const lt = draft.leadTimeDays.trim();
    const ltVal = lt === '' ? null : Number(lt);
    if (ltVal === null || Number.isInteger(ltVal)) {
      if (ltVal !== (quote.leadTimeDays ?? null)) b.leadTimeDays = ltVal;
    }

    text('fobLocation', quote.fobLocation);
    text('eccn', quote.eccn);
    text('htsCode', quote.htsCode);
    text('countryOfOrigin', quote.countryOfOrigin);
    text('hazmatClass', quote.hazmatClass);
    text('notes', quote.notes);
    return b;
  }

  const body = build();
  const dirty = Object.keys(body).length > 0;
  const docs = saved?.downstream ?? impact.data?.downstream ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="w-full max-w-2xl rounded-lg bg-surface shadow-lg my-8">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Correct vendor quote</h2>
            <p className="text-[11px] text-foreground-muted mt-0.5">
              {quote.vendorNameRaw}
              {quote.source !== 'manual' && (
                <> · parsed by {quote.parserModel ?? 'AI'}, so worth checking</>
              )}
            </p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3 text-xs">
          {saved ? (
            <div className="space-y-3">
              <div className="rounded bg-success-muted p-2 text-success font-medium">
                {saved.unchanged
                  ? 'Nothing changed — no correction was recorded.'
                  : `Corrected ${Object.keys(saved.changed).length} field${
                      Object.keys(saved.changed).length === 1 ? '' : 's'
                    }.`}
              </div>
              {!saved.unchanged && Object.keys(saved.changed).length > 0 && (
                <div className="rounded border border-border p-2 space-y-1">
                  {Object.entries(saved.changed).map(([k, v]) => (
                    <div key={k} className="flex gap-2 font-mono text-[11px]">
                      <span className="text-foreground-muted w-40 shrink-0">{k}</span>
                      <span className="text-danger line-through">{String(v.from ?? '—')}</span>
                      <span className="text-foreground-muted">→</span>
                      <span className="text-success">{String(v.to ?? '—')}</span>
                    </div>
                  ))}
                </div>
              )}
              {docs.length > 0 && <DocList docs={docs} />}
              <button onClick={onClose} className="btn-primary text-xs px-3 py-1">Done</button>
            </div>
          ) : (
            <>
              {docs.length > 0 && <DocList docs={docs} />}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Vendor name (as quoted)">
                  <input className={inputCls} value={draft.vendorNameRaw}
                    onChange={(e) => setDraft({ ...draft, vendorNameRaw: e.target.value })} />
                </Field>
                <Field label="Contact name">
                  <input className={inputCls} value={draft.vendorContactName}
                    onChange={(e) => setDraft({ ...draft, vendorContactName: e.target.value })} />
                </Field>
                <Field label="Contact email" hint="Appears on the vendor PO">
                  <input className={inputCls} value={draft.vendorContactEmail}
                    onChange={(e) => setDraft({ ...draft, vendorContactEmail: e.target.value })} />
                </Field>
                <Field label="Unit price">
                  <input className={inputCls} inputMode="decimal" value={draft.unitPrice}
                    onChange={(e) => setDraft({ ...draft, unitPrice: e.target.value })} />
                </Field>
                <Field label="Currency">
                  <select className={inputCls} value={draft.currency}
                    onChange={(e) => setDraft({ ...draft, currency: e.target.value })}>
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Quantity offered">
                  <input className={inputCls} inputMode="numeric" value={draft.qtyOffered}
                    onChange={(e) => setDraft({ ...draft, qtyOffered: e.target.value })} />
                </Field>
                <Field label="Condition">
                  <select className={inputCls} value={draft.conditionOffered}
                    onChange={(e) => setDraft({ ...draft, conditionOffered: e.target.value })}>
                    <option value="">—</option>
                    {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Certificate">
                  <select className={inputCls} value={draft.certOffered}
                    onChange={(e) => setDraft({ ...draft, certOffered: e.target.value })}>
                    <option value="">—</option>
                    {CERTS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Lead time (days)" hint="386 parsed quotes are missing this">
                  <input className={inputCls} inputMode="numeric" value={draft.leadTimeDays}
                    onChange={(e) => setDraft({ ...draft, leadTimeDays: e.target.value })} />
                </Field>
                <Field label="Incoterms">
                  <select className={inputCls} value={draft.incoterms}
                    onChange={(e) => setDraft({ ...draft, incoterms: e.target.value })}>
                    <option value="">—</option>
                    {INCOTERMS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="FOB location" hint="Shown to the customer as the origin">
                  <input className={inputCls} value={draft.fobLocation}
                    onChange={(e) => setDraft({ ...draft, fobLocation: e.target.value })} />
                </Field>
                <Field label="Country of origin" hint="Two-letter code">
                  <input className={inputCls} maxLength={2} value={draft.countryOfOrigin}
                    onChange={(e) =>
                      setDraft({ ...draft, countryOfOrigin: e.target.value.toUpperCase() })} />
                </Field>
                <Field label="ECCN">
                  <input className={inputCls} value={draft.eccn}
                    onChange={(e) => setDraft({ ...draft, eccn: e.target.value })} />
                </Field>
                <Field label="HTS code">
                  <input className={inputCls} value={draft.htsCode}
                    onChange={(e) => setDraft({ ...draft, htsCode: e.target.value })} />
                </Field>
                <Field label="Hazmat class">
                  <input className={inputCls} value={draft.hazmatClass}
                    onChange={(e) => setDraft({ ...draft, hazmatClass: e.target.value })} />
                </Field>
              </div>

              <Field label="Notes">
                <textarea className={inputCls} rows={2} value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
              </Field>

              {error && <div className="text-danger">{error}</div>}

              <div className="flex items-center justify-between border-t border-border pt-2">
                <span className="text-foreground-muted">
                  {dirty
                    ? `${Object.keys(body).length} field${
                        Object.keys(body).length === 1 ? '' : 's'} changed`
                    : 'No changes yet'}
                </span>
                <div className="flex gap-2">
                  <button onClick={onClose} className="btn-ghost text-xs px-3 py-1">Cancel</button>
                  <button
                    onClick={() => save.mutate(body)}
                    disabled={!dirty || save.isPending}
                    className="btn-primary text-xs px-3 py-1 flex items-center gap-1 disabled:opacity-50"
                  >
                    <Save className="w-3 h-3" />
                    {save.isPending ? 'Saving…' : 'Save correction'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
